import { createValidationError, getPointObjectType, required, validate, maxLength } from 'labguard-cdk'
import { fetchOptions } from 'src/services/fetch-options'

const maxLength32 = maxLength(32)

/**
 * Validates a group form object
 * @param managersIds the managers ids
 * @return {Object} An object with the field names which are in error as keys and the corresponding error messages
 * as values. The object will be empty if there was no error.
 */
export const validateGroup = ({ name, managersIds = [] }) => ({
  name: validate([required, maxLength32])(name),
  managersIds: validateManagers(managersIds),
})

/**
 * Prepare group object to be sent as a group input.
 */
export const asGroupInput = ({ id, ...group }) => group

const validateManagers = managersIds => {
  if (!managersIds.length) {
    return createValidationError('WEB_GROUP_MANAGER_ERROR')
  }
}
/**
 * Background image url, together with its dimensions (width and height).
 * @typedef {Object} Background
 * @property {string} url - background URL (may be a Blob or data URL).
 * @property {number} width - background width
 * @property {number} height - background height
 */
/**
 * Fetch background image and get its natural width and height.
 * @async
 * @param {string} url URL of the background image
 * @returns {Promise<Background>} Promise object represents a Background object
 * for the preloaded background image.
 */
export const preloadPlanBackground = url =>
  url
    ? fetch(url, fetchOptions)
        .then(response => {
          const contentType = response.headers.get('Content-Type')
          // try to guess if image is a SVG…
          if (contentType === 'image/svg+xml' || url.toLowerCase().endsWith('.svg')) {
            // …and if so, convert it to a data URL
            return response
              .text()
              .then(svgString => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`)
          } else {
            // else, convert the response to a Blob
            return response.blob().then(blob => URL.createObjectURL(blob))
          }
        })
        .then(
          url =>
            new Promise(resolve => {
              // load the Blob or data URL to an Image to retrieve image natural dimensions
              const image = new Image()
              const canvas = document.createElement('canvas')
              const context = canvas.getContext('2d')

              image.onload = function() {
                canvas.width = this.naturalWidth
                canvas.height = this.naturalHeight
                context.drawImage(this, 0, 0)

                resolve({
                  // if url is a data URL (ie. background is a SVG), keep it to prevent a rasterization of SVG image,
                  // else convert canvas to a data URL.
                  url: url.indexOf('data:') === 0 ? url : canvas.toDataURL(),
                  width: canvas.width,
                  height: canvas.height,
                })
              }

              image.src = url
            })
        )
    : Promise.resolve()

/**
 * converts a goup map point to a group map input.
 */
export const asPointsInput = points =>
  points.map(point => ({
    type: getPointObjectType(point).toUpperCase(),
    objectId: point[getPointObjectType(point)].id,
    coordinates: point.coordinates,
  }))

/**
 * Return true if a group has an alarm
 * @param {*} group
 */
export const groupHasNoAlarm = group => !group.criticalities.length && !group.winkingAlarm

/**
 * Retrieve the current color of a group
 * @param {*} group
 */
export const getGroupColor = group =>
  group.colors.length ? group.colors : [group.winkingAlarm ? 'rgba(255, 255, 255, .5)' : 'rgba(255, 255, 255, .1)']
